import React from "react";
import { 
  UserPlus, 
  Building2, 
  Settings2, 
  CheckCircle2, 
  ArrowRight,
  ShieldCheck,
  LayoutDashboard,
  MapPin,
  Search
} from "lucide-react";

// --- SUB-COMPONENT: CHECKLIST ITEM ---
const StepCard = ({ number, title, description, icon: Icon, status, onNavigate, targetId }) => (
  <div 
    onClick={() => onNavigate(targetId)}
    className={`group relative bg-white border ${status === 'complete' ? 'border-green-200 bg-green-50/30' : 'border-gray-200'} rounded-2xl p-6 transition-all hover:shadow-md cursor-pointer`}
  >
    <div className="flex gap-5">
      <div className="flex-shrink-0">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${status === 'complete' ? 'bg-green-100 text-green-600' : 'bg-purple-50 text-purple-600'}`}>
          <Icon size={24} />
        </div>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-black text-purple-500 uppercase tracking-widest">Step {number}</span>
          {status === 'complete' && <CheckCircle2 size={14} className="text-green-500" />}
        </div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-4">{description}</p>
        
        <button className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-all ${status === 'complete' ? 'text-green-600' : 'text-purple-600 hover:gap-3'}`}>
          {status === 'complete' ? 'Manage Settings' : 'Initialize Setup'} 
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  </div>
);

// --- MAIN PAGE COMPONENT ---
const AdminChecklist = ({ onNavigate }) => {
  return (
    <div className="space-y-10 pb-20">
      {/* Header */}
      <section className="text-center md:text-left">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 border border-purple-100 mb-4">
          <ShieldCheck size={14} className="text-purple-600" />
          <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest">Administrator Onboarding</span>
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Organization Setup</h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          Welcome to the Nuvio Command Center. Complete these three steps to launch your program 
          and begin tracking athlete development.
        </p>
      </section>

      {/* Checklist Grid - Updated targetIds to match AdminDocs state logic */}
      <div className="grid gap-4">
        <StepCard 
          number="1"
          title="Create Your Admin Account"
          description="Verify your institutional email and set up your administrative credentials to manage staff and rosters."
          icon={UserPlus}
          status="complete"
          targetId="create" // Matches 'create' in AdminDocs
          onNavigate={onNavigate}
        />

        <StepCard 
          number="2"
          title="Create & Brand Your Program"
          description="Establish your school or club in the Marketplace. Set your address to become discoverable."
          icon={Building2}
          status="pending"
          targetId="program" // Matches 'program' in AdminDocs
          onNavigate={onNavigate}
        />

        <StepCard 
          number="3"
          title="Define Combine & Skill Outcomes"
          description="Set the performance benchmarks and drill requirements for your athletes."
          icon={Settings2}
          status="pending"
          targetId="metrics" // Matches 'metrics' in AdminDocs
          onNavigate={onNavigate}
        />
      </div>

      {/* Discovery Importance Section */}
      <section className="bg-slate-900 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-500/20 p-2 rounded-lg text-blue-400">
                <MapPin size={24} />
              </div>
              <h3 className="text-xl font-bold">The Power of Location</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Setting your **Physical Address** in Step 2 is vital. The Nuvio Marketplace uses radius-based discovery to connect you with nearby talent.
            </p>
            <ul className="space-y-2 text-xs font-bold text-slate-300">
              <li className="flex items-center gap-2 text-red-400">
                <Search size={14} /> Without an address, athletes cannot find your program
              </li>
              <li className="flex items-center gap-2 text-green-400">
                <CheckCircle2 size={14} /> Verified locations appear on the "Nearby Programs" map
              </li>
            </ul>
          </div>

          <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 w-full md:w-64 text-center">
            <LayoutDashboard size={40} className="mx-auto text-purple-400 mb-2" />
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Scoring Engine</p>
            <p className="text-xs font-bold text-yellow-500 mt-1 italic tracking-tight">Awaiting Benchmarks</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminChecklist;